/**
 * Mirrors `appendFinalTokens` and `mergeInterimPreview` in desktop/src/common/meetingRecorder/transcriptMerge.ts; keep the
 * sentence cap, endpoint seal, word completion and interim speaker smoothing in step. A connection is one source, so
 * desktop's cross-source rejoin never applies.
 */

const SENTENCE_CAP = 2

/** Soniox often revises a brief mid-utterance speaker guess before finalizing; a flicker reads worse than a lag. */
const INTERIM_SPEAKER_SWITCH_MS = 600

export type RecordedToken = {
  text: string
  isFinal: boolean
  speakerId: number | null
  startMs: number
  endMs: number
  isEndpoint: boolean
}

export type DesktopRow = { speakerId: number; text: string; closedAtEndpoint: boolean }

export type DesktopViewRow = DesktopRow & {
  /** Not-yet-final text desktop paints after the row's final text. */
  interim: string
  /** The row exists only in the interim preview. */
  previewOnly: boolean
}

const sentenceCount = (text: string, cap: number): number => {
  const re = /[.!?]["']?(?:\s+[A-Z]|\s*$)/g
  let n = 0
  while (re.exec(text) !== null) {
    n += 1
    if (n >= cap) return n
  }
  return n
}

const normalizeTokenText = (text: string): string => text.replace(/[\n\r\t]+/g, ' ')

// The constructor form, because tsconfig targets es5 and rejects a `u` literal; every supported browser has it.
const UNSPACED_SCRIPT_START = new RegExp(
  '^[\\p{Script=Han}\\p{Script=Hiragana}\\p{Script=Katakana}\\p{Script=Thai}\\p{Script=Lao}\\p{Script=Khmer}\\p{Script=Myanmar}]',
  'u'
)

const continuesWord = (text: string): boolean => text !== '' && !/^\s/.test(text) && !UNSPACED_SCRIPT_START.test(text)

export function appendFinalTokens(rows: DesktopRow[], tokens: readonly RecordedToken[]): void {
  for (const token of tokens) {
    if (!token.isFinal) continue
    // Desktop resolves a final without a diarized id to speaker 0.
    const speakerId = token.speakerId ?? 0
    const text = normalizeTokenText(token.text)
    const last = rows[rows.length - 1]
    const accepts =
      last !== undefined &&
      last.speakerId === speakerId &&
      !last.closedAtEndpoint &&
      sentenceCount(last.text, SENTENCE_CAP) < SENTENCE_CAP
    const row = accepts ? last : { speakerId, text: '', closedAtEndpoint: false }
    if (!accepts) rows.push(row)
    row.text += text
    if (token.isEndpoint || sentenceCount(row.text, SENTENCE_CAP) >= SENTENCE_CAP) row.closedAtEndpoint = true
  }
}

const completeWordTokens = (tokens: readonly RecordedToken[]): readonly RecordedToken[] => {
  const last = tokens[tokens.length - 1]
  if (last === undefined || /[.,!?;:]["']?$/.test(last.text)) return tokens
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (!continuesWord(normalizeTokenText(tokens[i].text))) return tokens.slice(0, i)
  }
  return []
}

/** Desktop's interim speaker: the proxy's 0 means "no speaker yet". */
const interimSpeaker = (token: RecordedToken): number | null =>
  token.speakerId !== null && token.speakerId > 0 ? token.speakerId : null

const stabilizeInterimSpeakers = (tokens: readonly RecordedToken[], sticky: number | null): RecordedToken[] => {
  const stabilized: RecordedToken[] = []
  let current = sticky
  for (let start = 0; start < tokens.length;) {
    const speaker = interimSpeaker(tokens[start])
    let end = start + 1
    while (
      end < tokens.length &&
      (interimSpeaker(tokens[end]) === speaker || (speaker !== null && interimSpeaker(tokens[end]) === null))
    )
      end++
    const spanMs = tokens[end - 1].endMs - tokens[start].startMs
    if (speaker !== null && speaker !== current && (current === null || spanMs >= INTERIM_SPEAKER_SWITCH_MS)) {
      current = speaker
    }
    for (let i = start; i < end; i++) {
      stabilized.push({ ...tokens[i], isFinal: true, speakerId: current ?? tokens[i].speakerId })
    }
    start = end
  }
  return stabilized
}

export function mergeInterimPreview(rows: readonly DesktopRow[], interim: readonly RecordedToken[]): DesktopViewRow[] {
  const view = rows.map((row) => ({ ...row, interim: '', previewOnly: false }))
  const tokens = stabilizeInterimSpeakers(completeWordTokens(interim), rows[rows.length - 1]?.speakerId ?? null)
  if (tokens.length === 0) return view
  // With one source the merger only ever appends to the newest row, so that is the only row to copy.
  const last = rows[rows.length - 1]
  const next: DesktopRow[] = last ? [{ ...last }] : []
  appendFinalTokens(next, tokens)
  if (last) view[view.length - 1].interim = next[0].text.slice(last.text.length)
  for (const row of next.slice(last ? 1 : 0)) {
    view.push({
      speakerId: row.speakerId,
      text: '',
      closedAtEndpoint: row.closedAtEndpoint,
      interim: row.text,
      previewOnly: true,
    })
  }
  return view
}

/** What desktop showed after `batches[through]`: every final so far, plus that batch's hypothesis as the interim. */
export function desktopViewAt(batches: readonly (readonly RecordedToken[])[], through: number): DesktopViewRow[] {
  const rows: DesktopRow[] = []
  for (let i = 0; i <= through && i < batches.length; i++) appendFinalTokens(rows, batches[i])
  return mergeInterimPreview(
    rows,
    (batches[through] ?? []).filter((token) => !token.isFinal)
  )
}
